import { logError } from 'services/logging/logging';
import { GenericError } from 'support/helpers/errors/errors';

import { NavigableItem } from './types';

const VIRTUALIZED_TABLES = ['line_items'];

export type ScrollToFieldOptions = {
  /**
   * Centring is right for cycling through errors, where an anchor that is already on screen still
   * has to visibly move. It is wrong once an anchor is taller than the scrollport, because centring
   * then pushes its top — the label and the inline error — out of view. Defaults to centring.
   */
  block?: ScrollLogicalPosition;
};

export const scrollToNavigableItem = async (
  item: NavigableItem,
  scrollToVirtualizedRow: (tableId: string, rowIndex: number) => void,
  containerRef?: React.RefObject<HTMLElement | null> | null,
  { block = 'center' }: ScrollToFieldOptions = {},
): Promise<void> => {
  const root = getScopeRoot(containerRef);
  const selector = `[data-field="${item.fieldPath}"]`;
  let element = root.querySelector(selector);

  if (!element) {
    const table = VIRTUALIZED_TABLES.find((table) => item.fieldPath.startsWith(table));
    if (table) {
      element = await getVirtualizedItem(item.fieldPath, table, scrollToVirtualizedRow, containerRef);
    } else {
      element = await waitForElement(root, selector);
      if (!element) {
        logError(new GenericError('WebEDI: Failed to find element with item: ', { item, selector }));
      }
    }
  }

  if (element && 'focus' in element) {
    (element as HTMLInputElement).focus();
    element.scrollIntoView({ behavior: 'smooth', block, inline: 'center' });
  }
};

export const getVirtualizedItem = async (
  fieldPath: string,
  domContainerId: string,
  scrollToVirtualizedRow: (tableId: string, rowIndex: number) => void,
  containerRef?: React.RefObject<HTMLElement | null> | null,
): Promise<Element | null> => {
  const root = getScopeRoot(containerRef);
  const virtualizedContainer = root.querySelector<HTMLElement>(`#${domContainerId}`);
  if (virtualizedContainer) {
    const index = getItemIndexSection(fieldPath);
    if (index !== -1) {
      scrollToVirtualizedRow(domContainerId, index);
    }
  }

  // Falls back to the container so the table is at least scrolled into view when the field itself
  // never materialises.
  return (await waitForElement(root, `[data-field="${fieldPath}"]`)) ?? virtualizedContainer;
};

/**
 * How long to wait for a field to appear before giving up on it.
 *
 * The target may not be in the DOM when navigation starts: the virtualizer has to scroll its row
 * into the window, and a collapsed embedded table has to expand. Both are React state updates that
 * commit after this call, so querying once finds nothing and focus lands nowhere.
 *
 * Measured in milliseconds rather than frames so the budget does not halve on a 120Hz display.
 */
const ELEMENT_WAIT_MS = 300;

/** Polls for `selector` frame by frame, so a target rendered by a pending state update is found. */
export const waitForElement = async (root: ParentNode, selector: string): Promise<Element | null> => {
  const deadline = Date.now() + ELEMENT_WAIT_MS;

  for (;;) {
    const element = root.querySelector(selector);
    if (element || Date.now() >= deadline) {
      return element;
    }
    await nextFrame();
  }
};

/**
 * Form errors are keyed in the order the resolver walked the specification, which is not necessarily
 * the order the fields are laid out in, so the field to jump to is resolved by DOM position.
 */
export const findFirstFieldPathInDomOrder = (
  fieldPaths: Array<string>,
  containerRef?: React.RefObject<HTMLElement | null> | null,
): string | undefined => {
  if (fieldPaths.length === 0) {
    return undefined;
  }

  const root = getScopeRoot(containerRef);
  const wantedFieldPaths = new Set(fieldPaths);

  for (const anchor of Array.from(root.querySelectorAll<HTMLElement>('[data-field]'))) {
    const fieldPath = anchor.dataset.field;
    if (fieldPath && wantedFieldPaths.has(fieldPath)) {
      return fieldPath;
    }
  }

  return undefined;
};

const getScopeRoot = (containerRef?: React.RefObject<HTMLElement | null> | null): ParentNode => {
  return containerRef?.current ?? document;
};

const getItemIndexSection = (fieldPath: string): number => {
  return parseInt(fieldPath.split('.')[1]);
};

const nextFrame = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 16);
  });
